import { useEffect, useMemo, useState } from 'react'
import { supabase } from './supabaseClient'
import './Schools.css'

const DIVISIONS    = ['All', 'D1', 'D2', 'D3', 'JUCO']
const MILES_OPTIONS = ['All', '< 100', '< 250', '< 500', '< 1000']

const DIV_LABEL = {
  D1:   'Division I Program',
  D2:   'Division II Program',
  D3:   'Division III Program — No Athletic Scholarships',
  JUCO: 'Junior College Program',
}

function haversine(lat1, lng1, lat2, lng2) {
  const R    = 3958.8
  const toRad = d => d * Math.PI / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a    = Math.sin(dLat / 2) ** 2
            + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return Math.round(R * 2 * Math.asin(Math.sqrt(a)))
}

export default function Schools({ user }) {
  useEffect(() => { document.title = 'Browse Programs — CommitHQ' }, [])

  const [schools, setSchools]           = useState([])
  const [loading, setLoading]           = useState(true)
  const [pipelineNames, setPipelineNames] = useState(new Set())
  const [addingIds, setAddingIds]       = useState(new Set())
  const [query, setQuery]               = useState('')
  const [divFilter, setDivFilter]       = useState('All')
  const [selectedSchool, setSelectedSchool] = useState(null)
  const [userLat, setUserLat]           = useState(null)
  const [userLng, setUserLng]           = useState(null)
  const [hometown, setHometown]         = useState('')
  const [milesFilter, setMilesFilter]   = useState('All')

  useEffect(() => {
    async function load() {
      const [schoolsRes, pipelineRes, profileRes] = await Promise.all([
        supabase
          .from('schools_db')
          .select('id, school_name, division, conference, state, head_coach, lat, lng')
          .order('school_name', { ascending: true }),
        supabase
          .from('pipeline_schools')
          .select('school_name')
          .eq('user_id', user.id),
        supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single(),
      ])

      const schoolList = schoolsRes.data || []
      setSchools(schoolList)
      const names = new Set(
        (pipelineRes.data || []).map(r => r.school_name.toLowerCase())
      )
      setPipelineNames(names)

      // Load user home coords from profiles
      const profile = profileRes.data
      console.log('[Schools] home_lat:', profile?.home_lat, 'home_lng:', profile?.home_lng)
      if (profile?.home_lat && profile?.home_lng) {
        setUserLat(parseFloat(profile.home_lat))
        setUserLng(parseFloat(profile.home_lng))
      }
      if (profile?.hometown) setHometown(profile.hometown)

      setLoading(false)
    }
    load()
  }, [user.id])

  const filtered = useMemo(() => {
    let list = schools
    if (divFilter !== 'All') {
      list = list.filter(s => s.division === divFilter)
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase()
      list = list.filter(s => s.school_name.toLowerCase().includes(q))
    }
    if (milesFilter !== 'All' && userLat && userLng) {
      const limit = parseInt(milesFilter.replace(/\D/g, ''), 10)
      list = list.filter(s => {
        if (!s.lat || !s.lng) return false
        return haversine(userLat, userLng, parseFloat(s.lat), parseFloat(s.lng)) < limit
      })
    }
    return list
  }, [schools, divFilter, query, milesFilter, userLat, userLng])

  async function handleAddToPipeline(school) {
    setAddingIds(prev => new Set([...prev, school.id]))
    const { error } = await supabase.from('pipeline_schools').insert({
      user_id:     user.id,
      school_name: school.school_name,
      division:    school.division,
      sport:       'Baseball',
      status:      'Researching',
    })
    if (!error) {
      setPipelineNames(prev => new Set([...prev, school.school_name.toLowerCase()]))
    }
    setAddingIds(prev => {
      const next = new Set(prev)
      next.delete(school.id)
      return next
    })
  }

  const inPipeline = (school) => pipelineNames.has(school.school_name.toLowerCase())

  return (
    <div className="schools-page">
      {/* ── Page header ── */}
      <div className="schools-header">
        <div>
          <h1 className="schools-title">Baseball Programs</h1>
          <p className="schools-subtitle">
            {loading ? 'Loading…' : `${schools.length} NCAA & JUCO programs`}
          </p>
        </div>
      </div>

      {/* ── Search + filters ── */}
      <div className="schools-controls">
        <div className="schools-search-wrap">
          <svg className="schools-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            className="schools-search"
            type="text"
            placeholder="Search programs…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          {query && (
            <button className="schools-search-clear" onClick={() => setQuery('')} aria-label="Clear search">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          )}
        </div>

        <div className="schools-filters-row">
          <div className="schools-div-filters">
            {DIVISIONS.map(d => (
              <button
                key={d}
                className={`div-filter-btn${divFilter === d ? ' active' : ''}${d !== 'All' ? ` div-filter-${d.toLowerCase()}` : ''}`}
                onClick={() => setDivFilter(d)}
              >
                {d}
              </button>
            ))}
          </div>

          {userLat && userLng && (
            <select
              className="schools-miles-select"
              value={milesFilter}
              onChange={e => setMilesFilter(e.target.value)}
            >
              {MILES_OPTIONS.map(o => (
                <option key={o} value={o}>
                  {o === 'All' ? 'Any distance' : `Within ${o.replace('< ', '')} miles`}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* ── Result count ── */}
      {!loading && (
        <p className="schools-count">
          Showing <strong>{filtered.length}</strong> of <strong>{schools.length}</strong> programs
        </p>
      )}

      {/* ── Grid ── */}
      {loading ? (
        <div className="schools-loading">
          <span className="schools-loading-spinner" />
          Loading programs…
        </div>
      ) : filtered.length === 0 ? (
        <div className="schools-empty">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <p>No programs match your search.</p>
        </div>
      ) : (
        <div className="schools-grid">
          {filtered.map(school => (
            <SchoolCard
              key={school.id}
              school={school}
              inPipeline={inPipeline(school)}
              adding={addingIds.has(school.id)}
              onAdd={() => handleAddToPipeline(school)}
              onCardClick={() => setSelectedSchool(school)}
            />
          ))}
        </div>
      )}

      {/* ── School info modal ── */}
      {selectedSchool && (
        <SchoolModal
          school={selectedSchool}
          inPipeline={inPipeline(selectedSchool)}
          adding={addingIds.has(selectedSchool.id)}
          onAdd={() => handleAddToPipeline(selectedSchool)}
          onClose={() => setSelectedSchool(null)}
          userLat={userLat}
          userLng={userLng}
          hometown={hometown}
        />
      )}
    </div>
  )
}

// ── School Card ───────────────────────────────────────────────────

function SchoolCard({ school, inPipeline, adding, onAdd, onCardClick }) {
  const divClass = {
    D1:   'div-badge-d1',
    D2:   'div-badge-d2',
    D3:   'div-badge-d3',
    JUCO: 'div-badge-juco',
  }[school.division] ?? 'div-badge-d3'

  return (
    <div className="school-card school-card-clickable" onClick={onCardClick}>
      <div className="school-card-top">
        <div className="school-card-name-row">
          <span className="school-card-name">{school.school_name}</span>
          <span className={`school-div-badge ${divClass}`}>{school.division}</span>
        </div>

        <div className="school-card-meta">
          {school.conference && (
            <span className="school-meta-item">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
              {school.conference}
            </span>
          )}
          {school.state && (
            <span className="school-meta-item">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              {school.state}
            </span>
          )}
          {school.head_coach && (
            <span className="school-meta-item school-meta-coach">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="4"/>
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
              </svg>
              {school.head_coach}
            </span>
          )}
        </div>
      </div>

      <div className="school-card-footer" onClick={e => e.stopPropagation()}>
        {inPipeline ? (
          <button className="btn-in-pipeline" disabled>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            In Pipeline
          </button>
        ) : (
          <button className="btn-add-pipeline" onClick={onAdd} disabled={adding}>
            {adding ? (
              'Adding…'
            ) : (
              <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add to Pipeline
              </>
            )}
          </button>
        )}
      </div>
    </div>
  )
}

// ── School Modal ──────────────────────────────────────────────────

function SchoolModal({ school, inPipeline, adding, onAdd, onClose, userLat, userLng, hometown }) {
  const [headCoach, setHeadCoach] = useState(null)

  useEffect(() => {
    async function fetchCoach() {
      const { data } = await supabase
        .from('coaches_db')
        .select('first_name, last_name, email')
        .eq('school_name', school.school_name)
        .eq('title', 'Head Coach')
        .limit(1)
        .single()
      setHeadCoach(data || null)
    }
    fetchCoach()
  }, [school.school_name])

  // Close on Escape
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const divClass = {
    D1:   'div-badge-d1',
    D2:   'div-badge-d2',
    D3:   'div-badge-d3',
    JUCO: 'div-badge-juco',
  }[school.division] ?? 'div-badge-d3'

  const distance = (userLat && userLng && school.lat && school.lng)
    ? haversine(userLat, userLng, parseFloat(school.lat), parseFloat(school.lng))
    : null

  const mapQuery = encodeURIComponent(`${school.school_name} ${school.state ?? ''}`.trim())

  return (
    <div className="school-modal-overlay" onClick={onClose}>
      <div className="school-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="school-modal-header">
          <div className="school-modal-title-wrap">
            <h2 className="school-modal-name">{school.school_name}</h2>
            <div className="school-modal-badges">
              {school.division && (
                <span className={`school-div-badge ${divClass}`}>{school.division}</span>
              )}
              {school.conference && (
                <span className="school-modal-conf">{school.conference}</span>
              )}
              {school.state && (
                <span className="school-modal-state">{school.state}</span>
              )}
            </div>
          </div>
          <button className="school-modal-close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Map */}
        <div className="school-modal-map-wrap">
          <iframe
            title={`Map of ${school.school_name}`}
            className="school-modal-map"
            src={`https://maps.google.com/maps?q=${mapQuery}&output=embed`}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

        {/* Body */}
        <div className="school-modal-body">
          {/* Division label */}
          {DIV_LABEL[school.division] && (
            <div className="school-modal-row">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 10.5L12 3l9 7.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-9.5z"/>
                <path d="M9 21V12h6v9"/>
              </svg>
              <span>{DIV_LABEL[school.division]}</span>
            </div>
          )}

          {/* Head coach from coaches_db */}
          {headCoach && (
            <div className="school-modal-row">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              <span>
                <strong>Head Coach:</strong>{' '}
                {headCoach.first_name} {headCoach.last_name}
                {headCoach.email && (
                  <a href={`mailto:${headCoach.email}`} className="school-modal-coach-email">
                    {' '}· {headCoach.email}
                  </a>
                )}
              </span>
            </div>
          )}

          {/* Distance */}
          <div className="school-modal-row">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            {distance !== null
              ? <span>{distance.toLocaleString()} miles from home</span>
              : <span className="school-modal-hint">Add your zip code in Settings to see distance</span>
            }
          </div>
        </div>

        {/* Footer CTA */}
        <div className="school-modal-footer">
          {inPipeline ? (
            <button className="btn-in-pipeline school-modal-btn-full" disabled>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              In Pipeline
            </button>
          ) : (
            <button className="btn-add-pipeline school-modal-btn-full" onClick={onAdd} disabled={adding}>
              {adding ? 'Adding…' : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add to Pipeline
                </>
              )}
            </button>
          )}
        </div>

      </div>
    </div>
  )
}
